import { getLocale, intlTag, localDate, t } from "../i18n";

/*
 * Display formatting. Money arrives as decimal strings and is converted once,
 * here, at the display edge. Digit grouping and dates follow the signed-in person's
 * choice (src/i18n, /admin/locale/me): Indian grouping (28,47,500) unless they pick
 * international (2,847,500); dates as "20 Oct 2026" unless they pick another format.
 */

const formats = new Map<string, Intl.NumberFormat>();
const nf = (key: "inr" | "inrExact" | "count") => {
  const tag = intlTag("number");
  const k = `${key}:${tag}`;
  let f = formats.get(k);
  if (!f) {
    f = new Intl.NumberFormat(tag, key === "count" ? {} : { style: "currency", currency: "INR", ...(key === "inr" ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 }) });
    formats.set(k, f);
  }
  return f;
};
const inr = { format: (n: number) => nf("inr").format(n) };
const inrExact = { format: (n: number) => nf("inrExact").format(n) };

export const fmtCount = (n: number) => nf("count").format(n);

export function fmtInr(value: string | null | undefined, exact = false): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return (exact ? inrExact : inr).format(n);
}

/** ₹2.4 Cr / ₹38.5 L / ₹45,000 — for tiles where the full figure is too long. */
export function fmtInrShort(value: string | null | undefined): string {
  if (!value) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (getLocale().number_format === "international") {
    if (Math.abs(n) >= 1e9) return `₹${(n / 1e9).toFixed(2).replace(/\.?0+$/, "")}B`;
    if (Math.abs(n) >= 1e6) return `₹${(n / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
    return inr.format(n);
  }
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2).replace(/\.?0+$/, "")} ${t("Cr")}`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(1).replace(/\.0$/, "")} ${t("L")}`;
  return inr.format(n);
}

const IST = "Asia/Kolkata";

export function fmtDate(value: string | null | undefined, f: string = getLocale().date_format): string {
  if (!value) return "—";
  // A bare date (2026-10-20) is a calendar day, not an instant.
  const d = value.length === 10 ? new Date(`${value}T00:00:00+05:30`) : new Date(value);
  if (f === "dd mmm yyyy") return localDate(d, { day: "numeric", month: "short", year: "numeric", timeZone: IST });
  const [y, m, day] = istDate(d.getTime()).split("-");
  return f === "dd-mm-yyyy" ? `${day}-${m}-${y}` : f === "mm/dd/yyyy" ? `${m}/${day}/${y}` : f === "yyyy-mm-dd" ? `${y}-${m}-${day}` : `${day}/${m}/${y}`;
}

/** A date format's example ("28 Oct 2026" in English, "28 অক্টোঃ 2026" in Bengali) for the format menus: the server's are English. */
export const dateFormatExample = (format: string) => fmtDate("2026-10-28", format);

export function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  return localDate(new Date(value), { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true, timeZone: IST }, true);
}

export function fmtTime(value: string): string {
  return new Date(value).toLocaleTimeString(intlTag(), { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: IST });
}

/** Today's calendar date in India, as YYYY-MM-DD. */
export function todayIst(): string {
  return istDate(Date.now());
}

export function istDate(ms: number): string {
  return new Date(ms + 5.5 * 3_600_000).toISOString().slice(0, 10);
}

/** The time of day of an instant in India, as HH:MM (24-hour), for a time input. */
export function istTimeOfDay(iso: string): string {
  return new Date(Date.parse(iso) + 5.5 * 3_600_000).toISOString().slice(11, 16);
}

/** "2026-10-20" + "14:30" in IST -> ISO instant. */
export function istInstant(date: string, time: string): string {
  return new Date(`${date}T${time}:00+05:30`).toISOString();
}

export function relDays(date: string | null): string {
  if (!date) return "";
  const diff = Math.round((Date.parse(`${date}T00:00:00+05:30`) - Date.parse(`${todayIst()}T00:00:00+05:30`)) / 86_400_000);
  if (diff === 0) return t("today");
  if (diff === 1) return t("tomorrow");
  if (diff === -1) return t("yesterday");
  return diff > 0 ? t("in {n} days", { n: diff }) : t("{n} days ago", { n: -diff });
}

export function initials(name: string | null | undefined): string {
  if (!name) return "—";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

export const pct = (part: number, whole: number) => (whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : "—");

/*
 * A stored value shown as a word ("past_due" -> "Past due"), in the screen's language (wave 8: these reached the screens raw).
 * ENUM_WORDS gives the English where capitalising is not enough. scripts/i18n-scan.mjs (enumTexts) reads it and the status,
 * mode, method and treatment types in src/api, so each word needs a translation like any screen text.
 */
export const ENUM_WORDS: Record<string, string> = {
  upi: "UPI",
  neft: "NEFT",
  imps: "IMPS",
  igst: "IGST",
  emandate: "e-Mandate",
  irn_recorded: "IRN recorded",
  irn_cancelled: "IRN cancelled",
  slm: "Straight line (SLM)",
  wdv: "Written down value (WDV)",
  ok: "OK",
  mfa_required: "Two-factor code needed",
  mfa_enrollment_required: "Two-factor setup needed",
  google_meet: "Google Meet",
  business_gst: "Registered business (GST)",
  business_none: "Unregistered business",
  nonbillable: "Non-billable",
};

export function cap(s: string): string {
  return t(ENUM_WORDS[s] ?? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " "));
}
